import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { Icon } from '@/components/icon';
import { activeSearchParamsCountState, resetAdvancedSearchParamsState } from '@/state/search';
import { SearchQueryInterface } from '@/types';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import { getAdvancedSearchSummaryLabel } from '../utils';

export interface AdvancedSearchSummaryProps {
  onSearch: () => void;
  searchParams: SearchQueryInterface | null;
}

const AdvancedSearchSummary = ({ onSearch, searchParams }: AdvancedSearchSummaryProps) => {
  const [resetRequested, setResetRequested] = useState(false);
  const activeSearchParamsCount = useRecoilValue(activeSearchParamsCountState);
  const resetAdvancedSearchParams = useSetRecoilState(resetAdvancedSearchParamsState);

  const handleResetAll = () => {
    // Reset filters
    resetAdvancedSearchParams(null);
    setResetRequested(true);

    trackUser.event('Advanced Search: Reset filters');
  };

  // This UseEffect watches for param changes to ensure
  // state has been updated before triggering a search again.
  useEffect(() => {
    // If a reset was requested and search params are now 0
    if (resetRequested && activeSearchParamsCount.total === 0) {
      // Perform route search
      onSearch();
      // Reset bool now its been called
      setResetRequested(false);
    }
  }, [resetRequested, activeSearchParamsCount]);

  const advancedSearchSummaryLabel = searchParams ? getAdvancedSearchSummaryLabel(searchParams) : undefined;

  const { base, labelsWrapper, placeholder, bold, clearButton } = styles();

  return advancedSearchSummaryLabel ? (
    <div className={base()}>
      <div className={labelsWrapper()}>
        <Icon name="rules" size="base" color="text.primary" className="mr-sm" />
        <b className={bold()}>Filters: {advancedSearchSummaryLabel}</b>
      </div>
      <button className={clearButton()} type="button" onClick={() => handleResetAll()}>
        Reset filters
      </button>
    </div>
  ) : (
    <div className={placeholder()} />
  );
};

const styles = tv({
  slots: {
    base: 'flex items-center justify-between bg-grey-200 px-5 py-2 text-sm leading-none text-grey-700',
    labelsWrapper: 'flex items-center',
    placeholder: 'h-2 bg-grey-200',
    bold: 'font-medium text-grey-900',
    clearButton: 'italic leading-none text-grey-700 hover:text-lightBlue-600',
  },
});

export { AdvancedSearchSummary };
